import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import SiteHeader from "./SiteHeader";
import { REGIONS, publishedCollections } from "./data/collections";
import thailandMap from "./data/thailand-map.json";
import { EXHIBITION_PATH, scrollToId } from "./routes";
import { bagParams, track } from "./analytics";
import { pick, useLang } from "./i18n";

// สีพื้นจังหวัดที่มีถุง แยกตามภาค (จังหวัดที่ยังไม่มีถุงเป็นสีเทา)
const REGION_FILL = {
  central: "#f6c9a3",
  north: "#bfe0a3",
  northeast: "#f2d47f",
  east: "#9fd3e8",
  west: "#d4bdea",
  south: "#93d8c2",
};

// ภาคของจังหวัดที่ยังไม่มีถุง (จังหวัดที่มีถุงใช้ region ของถุง)
const REGION_NO_BAG = {
  chainat: "central", nakhonnayok: "central", phichit: "central", singburi: "central", uthaithani: "central",
  maehongson: "north", phayao: "north", uttaradit: "north",
  buengkan: "northeast", nongbualamphu: "northeast",
  trat: "east",
  phatthalung: "south",
};

const COLLATORS = { th: new Intl.Collator("th"), en: new Intl.Collator("en") };

export default function ThailandMapPage() {
  const navigate = useNavigate();
  const { lang, t, withLang } = useLang();
  const coll = COLLATORS[lang];
  const nameOf = (p) => (lang === "en" ? p.nameEn : p.nameTh); // จังหวัดบนแผนที่
  const bagName = (c) => (lang === "en" ? c.provinceEn : c.provinceTh);
  const panelRef = useRef(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const [scale, setScale] = useState(1);
  const [query, setQuery] = useState("");

  // จังหวัด → ถุงทั้งหมดของจังหวัดนั้น (กรุงเทพฯ มีหลายใบ)
  const bagsByKey = useMemo(() => {
    const out = {};
    publishedCollections.forEach((c) => {
      (out[c.mapKey] = out[c.mapKey] || []).push(c);
    });
    return out;
  }, []);

  const provinces = thailandMap.provinces;
  const byKey = useMemo(() => Object.fromEntries(provinces.map((p) => [p.key, p])), [provinces]);
  const withBags = provinces.filter((p) => bagsByKey[p.key]).length;

  const selected = selectedKey ? byKey[selectedKey] : null;
  const selectedBags = selected ? bagsByKey[selected.key] || [] : [];
  const selectedRegion = selected ? (selectedBags[0] ? selectedBags[0].region : REGION_NO_BAG[selected.key]) : null;
  // ถุงของจังหวัดอื่นในภาคเดียวกัน เรียงตามชื่อ (ไทย ก–ฮ / อังกฤษ A–Z)
  const regionBags = selectedRegion
    ? publishedCollections
        .filter((c) => c.region === selectedRegion && c.mapKey !== selected.key)
        .sort((a, b) => coll.compare(bagName(a), bagName(b)) || coll.compare(a.editionEn || "", b.editionEn || ""))
    : [];
  const regionName = REGIONS.find((r) => r.id === selectedRegion)?.[lang];
  const label = byKey[hoverKey || selectedKey];

  const q = query.trim().toLowerCase();
  const matches = q
    ? provinces
        .filter((p) => p.nameTh.includes(q) || p.nameEn.toLowerCase().includes(q))
        .sort((a, b) => coll.compare(nameOf(a), nameOf(b)))
        .slice(0, 8)
    : [];

  // method: map (พื้นที่จังหวัด) | pin | search
  const select = (key, method = "map") => {
    setSelectedKey(key);
    const p = byKey[key];
    if (p) track("map_province_select", { province_th: p.nameTh, province_en: p.nameEn, has_bag: !!bagsByKey[key], method });
    setQuery("");
    // มือถือ: แผงรายละเอียดอยู่ใต้แผนที่ เลื่อนให้เห็น
    if (window.matchMedia && window.matchMedia("(max-width: 1023px)").matches) {
      setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
    }
  };

  const pinR = Math.max(2.5, 7 / scale);

  // list: province (ถุงของจังหวัดที่เลือก) | region (ถุงจังหวัดอื่นในภาค)
  const openBag = (c, list) => {
    track("map_bag_click", { ...bagParams(c), from_province: selected?.nameTh || "", list });
    navigate(withLang(`${EXHIBITION_PATH}/${c.slug}`), { state: { source: "map" } });
  };

  return (
    <div className="bce" id="bce-top" lang={lang}>
      <Helmet>
        <html lang={lang} />
        <title>{t("mapPageTitle")}</title>
      </Helmet>
      <SiteHeader
        links={[
          { label: t("navBags"), onClick: () => navigate(withLang(EXHIBITION_PATH)) },
          { label: t("navMap"), active: true, onClick: () => scrollToId("bce-top") },
        ]}
      />

      <main className="bce-hero bce-map-page">
        <div className="bce-map-layout">
          <section className="bce-map-card" aria-label={t("mapAria")}>
            <div className="bce-map-head">
              <div>
                <h1>{t("mapTitle")}</h1>
                <p>{t("mapIntro", withBags, provinces.length, publishedCollections.length)}</p>
              </div>
              <div className="bce-map-search">
                <input
                  type="search"
                  className="bce-gallery-search"
                  placeholder={t("mapSearch")}
                  aria-label={t("mapSearchLabel")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {matches.length > 0 && (
                  <ul className="bce-map-suggest">
                    {matches.map((p) => (
                      <li key={p.key}>
                        <button type="button" onClick={() => select(p.key, "search")}>
                          {nameOf(p)} <small>{lang === "en" ? p.nameTh : p.nameEn}</small>
                          {!bagsByKey[p.key] && <em> · {t("noBagYet")}</em>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <TransformWrapper minScale={1} maxScale={6} wheel={{ step: 0.15 }} doubleClick={{ disabled: true }} onTransformed={(_, s) => setScale(s.scale)}>
              {({ zoomIn, zoomOut, resetTransform }) => (
                <div className="bce-map-stage">
                  <TransformComponent wrapperClass="bce-map-canvas" wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%", height: "100%" }}>
                    <svg className="bce-map-svg" viewBox={thailandMap.viewBox} role="group" aria-label={t("provincesAria")}>
                      {provinces.map((p) => {
                        const bags = bagsByKey[p.key];
                        const fill = bags ? REGION_FILL[bags[0].region] : "#e3e5e8";
                        return (
                          <path
                            key={p.key}
                            d={p.d}
                            className={`bce-map-province${bags ? " has-bag" : ""}${selectedKey === p.key ? " is-selected" : ""}${hoverKey === p.key ? " is-hover" : ""}`}
                            style={{ fill }}
                            onClick={() => select(p.key)}
                            onMouseEnter={() => setHoverKey(p.key)}
                            onMouseLeave={() => setHoverKey(null)}
                          >
                            <title>{nameOf(p)}</title>
                          </path>
                        );
                      })}
                      {provinces
                        .filter((p) => bagsByKey[p.key])
                        .map((p) => {
                          const n = bagsByKey[p.key].length;
                          return (
                            <g
                              key={`pin-${p.key}`}
                              className={`bce-map-pin${selectedKey === p.key ? " is-selected" : ""}`}
                              transform={`translate(${p.x} ${p.y})`}
                              role="button"
                              tabIndex={0}
                              aria-label={t("pinLabel", nameOf(p), n)}
                              onClick={() => select(p.key, "pin")}
                              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), select(p.key, "pin"))}
                              onMouseEnter={() => setHoverKey(p.key)}
                              onMouseLeave={() => setHoverKey(null)}
                            >
                              <circle r={pinR * 2.2} className="bce-map-pin-hit" />
                              <circle r={pinR} className="bce-map-pin-dot" />
                              {n > 1 && (
                                <text className="bce-map-pin-count" y={pinR * 0.38} fontSize={pinR * 1.1}>
                                  {n}
                                </text>
                              )}
                            </g>
                          );
                        })}
                      {label && (
                        <text className="bce-map-label" x={label.x} y={label.y - pinR * 1.8} fontSize={Math.max(5, 13 / scale)}>
                          {nameOf(label)}
                        </text>
                      )}
                    </svg>
                  </TransformComponent>
                  <div className="bce-map-tools">
                    <button type="button" aria-label={t("mapZoomIn")} onClick={() => zoomIn()}>
                      ＋
                    </button>
                    <button type="button" aria-label={t("mapZoomOut")} onClick={() => zoomOut()}>
                      −
                    </button>
                    <button type="button" aria-label={t("mapReset")} onClick={() => resetTransform()}>
                      ⟲
                    </button>
                  </div>
                </div>
              )}
            </TransformWrapper>

            <ul className="bce-map-legend" aria-label={t("legendLabel")}>
              {REGIONS.map((r) => (
                <li key={r.id}>
                  <span style={{ background: REGION_FILL[r.id] }} aria-hidden="true" />
                  {r[lang]}
                </li>
              ))}
              <li>
                <span style={{ background: "#e3e5e8" }} aria-hidden="true" />
                {t("noBagYet")}
              </li>
            </ul>
            <p className="bce-map-attrib">
              {lang === "en" ? "Province boundaries: geoBoundaries / © OpenStreetMap contributors (ODbL)" : thailandMap.attribution}
            </p>
          </section>

          <aside className="bce-story bce-map-panel" ref={panelRef} aria-live="polite">
            {selected ? (
              <>
                <h2>{nameOf(selected)}</h2>
                <p className="bce-story-sub">{lang === "en" ? selected.nameTh : selected.nameEn}</p>
                {selectedBags.length ? (
                  <div className="bce-map-bags">
                    {selectedBags.map((c) => (
                      <button key={c.id} type="button" className="bce-map-bag" onClick={() => openBag(c, "province")}>
                        <span className="bce-gallery-thumb" style={{ "--handle": c.handleColor }}>
                          <img src={c.thumbnail} alt="" />
                        </span>
                        <span className="bce-map-bag-text">
                          <b>{c.editionEn || c.provinceEn}</b>
                          <small>{pick(c, "teaser", lang)}</small>
                          <span className="bce-map-bag-cta">{t("watch360")}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="bce-story-text">{t("noBagProvince")}</p>
                )}
                {regionBags.length > 0 && (
                  <>
                    <h3 className="bce-map-region-title">
                      {t("otherInRegion", regionName)} <small>({regionBags.length})</small>
                    </h3>
                    <div className="bce-map-bags is-compact">
                      {regionBags.map((c) => (
                        <button key={c.id} type="button" className="bce-map-bag" onClick={() => openBag(c, "region")}>
                          <span className="bce-gallery-thumb" style={{ "--handle": c.handleColor }}>
                            <img src={c.thumbnail} alt="" loading="lazy" />
                          </span>
                          <span className="bce-map-bag-text">
                            <b>
                              {bagName(c)}
                              {c.editionEn ? ` · ${c.editionEn}` : ""}
                            </b>
                            <small>{pick(c, "teaser", lang)}</small>
                            <span className="bce-map-bag-cta">{t("watch360")}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <h2>{t("chooseProvince")}</h2>
                <p className="bce-story-sub">{t("chooseProvinceSub")}</p>
                <p className="bce-story-text">{t("mapHelp")}</p>
              </>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
